import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import { Children, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import _debounce from 'lodash.debounce';
import styled from 'styled-components';
import { useInterval } from '@crehana/react-hooks';
import useMediaQuery from "../hooks/useMediaQuery";
import Pagination from "../Pagination";
import DesktopPagination from "./DesktopPagination";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var SlideGroup = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-68nbq2-0"
})(["\n  transform: ", ";\n  transition: all 0.3s ease;\n"], function (props) {
  return "translateX(".concat(props.translateX * -1, "px)");
});
var GAP = {
  xs: 8,
  sm: 16,
  md: 24,
  lg: 24,
  xl: 24
};
var Slide = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-68nbq2-1"
})(["\n  width: 100%;\n  ", " {\n    width: ", ";\n  }\n  ", " {\n    width: ", ";\n  }\n  ", " {\n    width: ", ";\n  }\n  @media (min-width: 1920px) {\n    width: ", ";\n  }\n"], function (props) {
  return props.theme.breakpoints.up('sm');
}, function (_ref) {
  var itemsPerPageResponsive = _ref.itemsPerPageResponsive,
    isRemainingSlide = _ref.isRemainingSlide;
  return isRemainingSlide ? "calc(".concat(100 / itemsPerPageResponsive.sm, "% - ").concat(GAP.sm, "px)") : "".concat(100 / itemsPerPageResponsive.sm, "%");
}, function (props) {
  return props.theme.breakpoints.up('md');
}, function (_ref2) {
  var itemsPerPageResponsive = _ref2.itemsPerPageResponsive,
    isRemainingSlide = _ref2.isRemainingSlide;
  return isRemainingSlide ? "calc(".concat(100 / itemsPerPageResponsive.md, "% - ").concat(GAP.md, "px)") : "".concat(100 / itemsPerPageResponsive.md, "%");
}, function (props) {
  return props.theme.breakpoints.up('lg');
}, function (_ref3) {
  var itemsPerPageResponsive = _ref3.itemsPerPageResponsive,
    isRemainingSlide = _ref3.isRemainingSlide;
  return isRemainingSlide ? "calc(".concat(100 / itemsPerPageResponsive.lg, "% - ").concat(GAP.lg, "px)") : "".concat(100 / itemsPerPageResponsive.lg, "%");
}, function (_ref4) {
  var itemsPerPageResponsive = _ref4.itemsPerPageResponsive,
    isRemainingSlide = _ref4.isRemainingSlide;
  return isRemainingSlide ? "calc(".concat(100 / (itemsPerPageResponsive.xl || itemsPerPageResponsive.lg), "% - ").concat(GAP.xl, "px)") : "".concat(100 / (itemsPerPageResponsive.xl || itemsPerPageResponsive.lg), "%");
});
var slideFormatter = function slideFormatter(_ref5) {
  var arrChildren = _ref5.arrChildren,
    chunkSize = _ref5.chunkSize;
  var result = [];
  while (arrChildren.length > 0) {
    result.push(arrChildren.splice(0, chunkSize));
  }
  return result;
};
var SliderGrid = function SliderGrid(_ref6, ref) {
  var itemsPerPageProp = _ref6.itemsPerPage,
    desktopPaginationSelector = _ref6.desktopPaginationSelector,
    _ref6$desktopPaginati = _ref6.desktopPaginationType,
    desktopPaginationType = _ref6$desktopPaginati === void 0 ? 'carousel' : _ref6$desktopPaginati,
    mobilePaginationProps = _ref6.mobilePaginationProps,
    tabletPaginationProps = _ref6.tabletPaginationProps,
    desktopPaginationProps = _ref6.desktopPaginationProps,
    hidePagination = _ref6.hidePagination,
    dark = _ref6.dark,
    itemsWithShadow = _ref6.itemsWithShadow,
    onSlideChangeProp = _ref6.onSlideChange,
    _ref6$className = _ref6.className,
    className = _ref6$className === void 0 ? '' : _ref6$className,
    children = _ref6.children,
    dataTestId = _ref6['data-testid'],
    autoplay = _ref6.autoplay,
    _ref6$disableDrag = _ref6.disableDrag,
    disableDrag = _ref6$disableDrag === void 0 ? false : _ref6$disableDrag,
    isBannerSlider = _ref6.isBannerSlider;
  var touchRef = useRef({
    startX: 0,
    moveX: 0
  });
  var _useMediaQuery = useMediaQuery({
      only: 'sm'
    }),
    isSm = _useMediaQuery.matches;
  var _useMediaQuery2 = useMediaQuery({
      only: 'md'
    }),
    isMd = _useMediaQuery2.matches;
  var _useMediaQuery3 = useMediaQuery({
      only: 'lg'
    }),
    isLg = _useMediaQuery3.matches;
  var _useMediaQuery4 = useMediaQuery('1920px'),
    isXl = _useMediaQuery4.matches;
  var itemsPerPageResponsive = isBannerSlider ? {
    xs: 1,
    sm: 1,
    md: 1,
    lg: 1,
    xl: 1
  } : _objectSpread(_objectSpread({}, itemsPerPageProp), {}, {
    xs: (itemsPerPageProp === null || itemsPerPageProp === void 0 ? void 0 : itemsPerPageProp.xs) || 1,
    sm: (itemsPerPageProp === null || itemsPerPageProp === void 0 ? void 0 : itemsPerPageProp.sm) || 2,
    md: (itemsPerPageProp === null || itemsPerPageProp === void 0 ? void 0 : itemsPerPageProp.md) || 3,
    lg: (itemsPerPageProp === null || itemsPerPageProp === void 0 ? void 0 : itemsPerPageProp.lg) || 4,
    xl: (itemsPerPageProp === null || itemsPerPageProp === void 0 ? void 0 : itemsPerPageProp.xl) || (itemsPerPageProp === null || itemsPerPageProp === void 0 ? void 0 : itemsPerPageProp.lg) || 4
  });
  var itemsPerPage = isSm ? itemsPerPageResponsive.sm : itemsPerPageResponsive.xs;
  var gap = isSm ? GAP.sm : GAP.xs;
  if (isMd) {
    itemsPerPage = itemsPerPageResponsive.md;
    gap = GAP.md;
  }
  if (isLg) {
    itemsPerPage = itemsPerPageResponsive.lg;
    gap = GAP.lg;
  }
  if (isXl) {
    itemsPerPage = itemsPerPageResponsive.xl;
    gap = GAP.xl;
  }
  var sliderContainer = useRef(null);
  var _useState = useState(1),
    _useState2 = _slicedToArray(_useState, 2),
    currentPage = _useState2[0],
    setCurrentPage = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    slideWidth = _useState4[0],
    setSlideWidth = _useState4[1];
  var _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    childrenArray = _useState6[0],
    setChildrenArray = _useState6[1];
  var _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    translateX = _useState8[0],
    setTranslateX = _useState8[1];
  var _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    stopAutoplay = _useState0[0],
    setStopAutoplay = _useState0[1];

  // const [lastClonedIdx, setLastClonedIdx] = React.useState(0);

  var totalSlides = Children.toArray(children).length;
  var totalPages = childrenArray.length;
  var onSlideChange = useCallback(function (slide) {
    setCurrentPage(slide);
    if (typeof onSlideChangeProp === 'function') {
      onSlideChangeProp(slide);
    }
  }, [onSlideChangeProp]);
  if (ref && typeof ref !== 'function') {
    ref.current = {
      setSlide: onSlideChange
    };
  }
  useEffect(function () {
    // Set up behavior after change
    if (sliderContainer && sliderContainer.current) {
      var charr = childrenArray[currentPage - 1];
      if (charr && charr.length % itemsPerPage !== 0) {
        var v = childrenArray.slice(-1)[0];
        setTranslateX(translateX + v.length / itemsPerPage * (slideWidth + gap));
      } else {
        setTranslateX((currentPage - 1) * (slideWidth + gap));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // React.useEffect(() => {
  //   if (infinite) {
  //     setChildrenArray(slideGroup => {
  //       let temp = slideGroup[slideGroup.length - 1];

  //       if (temp && temp.length < itemsPerPage) {
  //         setLastClonedIdx(itemsPerPage - temp.length);

  //         temp = [
  //           ...temp,
  //           ...React.Children.toArray(children).slice(0, lastClonedIdx),
  //         ];
  //         slideGroup[slideGroup.length - 1] = temp;
  //       } else if (currentPage === totalPages) {
  //         const next = React.Children.toArray(children).slice(
  //           lastClonedIdx,
  //           lastClonedIdx + itemsPerPage,
  //         );

  //         if (next.length < itemsPerPage) {
  //           const newNext = [
  //             ...next,
  //             ...React.Children.toArray(children).slice(
  //               0,
  //               itemsPerPage - next.length,
  //             ),
  //           ];

  //           setLastClonedIdx(itemsPerPage - next.length);
  //           slideGroup.push(newNext);
  //         } else {
  //           setLastClonedIdx(lastClonedIdx + itemsPerPage);
  //           slideGroup.push(next);
  //         }
  //       }

  //       return slideGroup;
  //     });
  //   }
  // }, [currentPage, lastClonedIdx, infinite]);

  useEffect(function () {
    if (!(autoplay !== null && autoplay !== void 0 && autoplay.ignoreHover)) {
      var pauseAutoplay = function pauseAutoplay() {
        if (!stopAutoplay) {
          setStopAutoplay(true);
        }
      };
      var continueAutoplay = function continueAutoplay() {
        setStopAutoplay(false);
      };
      var sliderContainerRef = sliderContainer.current;
      if (autoplay !== null && autoplay !== void 0 && autoplay.duration && sliderContainer && sliderContainerRef) {
        sliderContainerRef.addEventListener('mouseover', pauseAutoplay);
        sliderContainerRef.addEventListener('mouseout', continueAutoplay);
      }
      return function () {
        if (sliderContainerRef) {
          sliderContainerRef.removeEventListener('mouseover', pauseAutoplay);
          sliderContainerRef.removeEventListener('mouseout', continueAutoplay);
        }
      };
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return function () {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay]);
  useInterval(function () {
    if (!stopAutoplay) {
      if (currentPage === totalPages) {
        onSlideChange(1);
      } else {
        onSlideChange(currentPage + 1);
      }
    }
  }, (autoplay === null || autoplay === void 0 ? void 0 : autoplay.duration) || null);
  useEffect(function () {
    // When viewport resizes, it should go to first page
    var onResize = _debounce(function () {
      setSlideWidth(function (prevWidth) {
        var _sliderContainer$curr;
        if (sliderContainer && sliderContainer.current && prevWidth !== sliderContainer.current.clientWidth) {
          if (currentPage > 1) {
            onSlideChange(1);
          }
        }
        return (sliderContainer === null || sliderContainer === void 0 ? void 0 : (_sliderContainer$curr = sliderContainer.current) === null || _sliderContainer$curr === void 0 ? void 0 : _sliderContainer$curr.clientWidth) || 0;
      });
    }, 100);
    if (sliderContainer && sliderContainer.current) {
      setSlideWidth(sliderContainer.current.clientWidth);
    }
    window.addEventListener('resize', onResize);
    return function () {
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliderContainer, currentPage]);
  useEffect(function () {
    if (!(isMd || isLg || isXl) && !disableDrag) {
      var dragTreshold = slideWidth / 10;
      var onTouchStart = function onTouchStart(event) {
        // drag Start
        var t = event.touches[0];
        touchRef.current.startX = t.pageX;
      };
      var onTouchMove = function onTouchMove(event) {
        var _event$touches$;
        // drag End
        touchRef.current.moveX = (_event$touches$ = event.touches[0]) === null || _event$touches$ === void 0 ? void 0 : _event$touches$.pageX;
        var translateXValue = (currentPage - 1) * slideWidth + (currentPage - 1) * gap + (touchRef.current.startX - touchRef.current.moveX);
        setTranslateX(translateXValue);
      };
      var onTouchEnd = function onTouchEnd() {
        var lastTranslateXValue = (currentPage - 1) * slideWidth + (currentPage - 1) * gap;
        if (translateX > lastTranslateXValue + dragTreshold && translateX < slideWidth * totalSlides) {
          if (currentPage === totalPages) {
            onSlideChange(currentPage - 1);
          }
          if (currentPage - 1 <= totalPages) {
            onSlideChange(currentPage + 1);
          }
        } else if (translateX + dragTreshold < lastTranslateXValue && translateX > 0) {
          if (currentPage !== 1) {
            onSlideChange(currentPage - 1);
          }
        } else {
          setTranslateX(lastTranslateXValue);
        }
      };
      if (sliderContainer && sliderContainer.current) {
        sliderContainer.current.addEventListener('touchstart', onTouchStart);
        sliderContainer.current.addEventListener('touchmove', onTouchMove);
        sliderContainer.current.addEventListener('touchend', onTouchEnd);
      }
      var sliderContainerNode = sliderContainer.current;
      return function () {
        if (sliderContainerNode) {
          sliderContainerNode.removeEventListener('touchstart', onTouchStart);
          sliderContainerNode.removeEventListener('touchmove', onTouchMove);
          sliderContainerNode.removeEventListener('touchend', onTouchEnd);
        }
      };
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return function () {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMd, isLg, isXl, disableDrag, slideWidth, currentPage, translateX, totalSlides, gap, totalPages]);
  useEffect(function () {
    setChildrenArray(slideFormatter({
      arrChildren: Children.toArray(children),
      chunkSize: itemsPerPage
    }));
  }, [itemsPerPage, children]);
  var shouldRenderPaginationComponent = !hidePagination && totalSlides > itemsPerPage;
  var shouldApplyTabletProps = isSm && tabletPaginationProps;
  return /*#__PURE__*/_jsxs("div", {
    className: "relative",
    children: [/*#__PURE__*/_jsx("div", {
      className: "flex items-center w-full min-w-full overflow-hidden max-w-full relative ".concat(className, " ").concat(itemsWithShadow ? 'p-4' : ''),
      "data-testid": dataTestId,
      ref: sliderContainer,
      children: childrenArray.map(function (slides, i) {
        return /*#__PURE__*/_jsx(SlideGroup, {
          translateX: translateX,
          className: "flex ".concat(slides.length === itemsPerPage ? 'justify-between' : 'justify-start'),
          style: {
            width: slideWidth + gap,
            minWidth: slideWidth + gap,
            maxWidth: slideWidth + gap
          },
          children: slides.map(function (slide, idx) {
            return /*#__PURE__*/_jsx(Slide, {
              className: "mr-8 sm:mr-16 md:mr-24 lg:mr-24",
              itemsPerPageResponsive: itemsPerPageResponsive,
              isRemainingSlide: slides.length < itemsPerPage,
              children: slide
            }, "slide-".concat(idx * 1));
          })
        }, "slide-group-".concat(i * 1));
      })
    }), shouldRenderPaginationComponent && (isBannerSlider ? /*#__PURE__*/_jsx("div", {
      className: "absolute inset-x-0 bottom-0 flex justify-center pb-24",
      children: /*#__PURE__*/_jsx(Pagination, {
        totalPages: totalPages,
        currentPage: currentPage,
        onPageChange: onSlideChange,
        isTransparent: true,
        type: "dots"
      })
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Pagination, _objectSpread({
        className: cn('flex justify-center w-full mt-24', shouldApplyTabletProps ? 'md:hidden' : 'sm:hidden'),
        type: "default-s",
        totalPages: totalPages,
        currentPage: currentPage,
        onPageChange: onSlideChange,
        dark: dark
      }, shouldApplyTabletProps ? tabletPaginationProps : mobilePaginationProps)), desktopPaginationSelector && /*#__PURE__*/_jsx(DesktopPagination, _objectSpread({
        selector: desktopPaginationSelector,
        className: "hidden sm:flex",
        type: desktopPaginationType,
        totalPages: totalPages,
        currentPage: currentPage,
        onPageChange: onSlideChange,
        dark: dark
      }, desktopPaginationProps))]
    }))]
  });
};
export default /*#__PURE__*/forwardRef(SliderGrid);